import { AfterViewInit, Directive, ElementRef, OnDestroy, inject } from '@angular/core';
import {
  isPackageMeasureKeyAllowed,
  PACKAGE_MEASURE_MAX_LENGTH,
  sanitizePackageMeasureInput,
} from './package-measure-input';

const PURO_INPUT_FIELD_SELECTOR = '.puro-input__field';

/**
 * Guards package weight/dimension `puro-input` fields to ESN/OM typing rules
 * (digits + one `.`, ≤2 decimals, max 6 chars, no negatives/symbols).
 */
@Directive({
  selector: 'puro-input[appPackageMeasureInput]',
})
export class PackageMeasureInputDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private nativeInput: HTMLInputElement | null = null;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!this.nativeInput) {
      return;
    }

    const allowed = isPackageMeasureKeyAllowed(
      {
        key: event.key,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        altKey: event.altKey,
      },
      this.nativeInput.value,
      {
        start: this.nativeInput.selectionStart ?? this.nativeInput.value.length,
        end: this.nativeInput.selectionEnd ?? this.nativeInput.value.length,
      },
    );

    if (!allowed) {
      event.preventDefault();
    }
  };

  private readonly onInput = (): void => {
    this.applySanitizedValue();
  };

  private readonly onPaste = (): void => {
    // Paste updates value asynchronously; sanitize on the next input tick via onInput.
    queueMicrotask(() => this.applySanitizedValue());
  };

  /**
   * Binds keydown/input/paste guards on the kit native input after render.
   */
  ngAfterViewInit(): void {
    const nativeInput = this.elementRef.nativeElement.querySelector(PURO_INPUT_FIELD_SELECTOR);
    if (!(nativeInput instanceof HTMLInputElement)) {
      return;
    }

    this.nativeInput = nativeInput;
    nativeInput.setAttribute('maxlength', String(PACKAGE_MEASURE_MAX_LENGTH));
    nativeInput.addEventListener('keydown', this.onKeyDown);
    nativeInput.addEventListener('input', this.onInput);
    nativeInput.addEventListener('paste', this.onPaste);
  }

  /**
   * Removes native listeners when the host is destroyed.
   */
  ngOnDestroy(): void {
    if (!this.nativeInput) {
      return;
    }

    this.nativeInput.removeEventListener('keydown', this.onKeyDown);
    this.nativeInput.removeEventListener('input', this.onInput);
    this.nativeInput.removeEventListener('paste', this.onPaste);
    this.nativeInput = null;
  }

  /**
   * Rewrites the native value when paste/input introduces invalid measure characters.
   */
  private applySanitizedValue(): void {
    if (!this.nativeInput) {
      return;
    }

    const sanitized = sanitizePackageMeasureInput(this.nativeInput.value);
    if (sanitized === this.nativeInput.value) {
      return;
    }

    const selectionStart = this.nativeInput.selectionStart ?? sanitized.length;
    this.nativeInput.value = sanitized;
    const nextCaret = Math.min(selectionStart, sanitized.length);
    try {
      this.nativeInput.setSelectionRange(nextCaret, nextCaret);
    } catch {
      // type=number inputs may reject selection APIs in some browsers
    }
    this.nativeInput.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
